import {HttpClient,HttpContext,HttpContextToken,HttpErrorResponse,HttpEvent,HttpHandlerFn,HttpInterceptorFn,HttpRequest,HttpResponse} from '@angular/common/http';
import {BehaviorSubject,catchError,concat,filter,Observable,of,switchMap,take,tap,throwError} from 'rxjs';
import {environment} from '../../environments/environment';
import {inject,PLATFORM_ID} from '@angular/core';
import {isPlatformServer} from '@angular/common';

interface GraphqlError{
	message?:string;
	extensions?:{
		response?:{message?:string};
		originalError?:{statusCode?:number};
	};
}
interface InterceptorError{
	message?:string;
	error?:unknown;
	graphQLErrors?:readonly GraphqlError[];
}

const isRefreshing=new BehaviorSubject<boolean>(false);
export const SKIP_REQUEST=new HttpContextToken<boolean>(():boolean=>true);

const sendError=(error:InterceptorError):Observable<never>=>{
	if(error.error instanceof ProgressEvent){
		return throwError(()=>new Error(error.message));
	}else if(error.error instanceof ErrorEvent){
		return throwError(()=>new Error(error.message));
	}else if(error.error instanceof HttpErrorResponse){
		const httpError=error.error;
		return throwError(()=>new Error(httpError.message || String(httpError)));
	}else{
		const graphqlError=error.graphQLErrors?.slice()?.shift()?.extensions?.response?.message;
		if(graphqlError){
			return throwError(()=>new Error(graphqlError));
		}else{
			return throwError(()=>error);
		}
	}
};

export const authenticationInterceptor:HttpInterceptorFn=(request:HttpRequest<unknown>,next:HttpHandlerFn):Observable<HttpEvent<unknown>>=>{
	const platformId=inject(PLATFORM_ID);
	const httpClient=inject(HttpClient);
	const refreshUrl=`${environment.apiServer.url}/api/authentication/refresh-jwt`;
	const context=new HttpContext().set(SKIP_REQUEST,true);
	
	if(isPlatformServer(platformId)) return next(request.clone());
	if(request.context.has(SKIP_REQUEST)) return next(request.clone());
	return next(request.clone())
	.pipe(
		filter((response)=>response.type!==0 && response instanceof HttpResponse),
		switchMap((response)=>{
			const errors:GraphqlError[]|undefined=(response as HttpResponse<{errors?:GraphqlError[]}>).body?.errors;
			if(errors){
				const unauthorizedError=errors.find((element)=>element?.extensions?.originalError?.statusCode===401);
				if(unauthorizedError){
					if(!isRefreshing.getValue()){
						isRefreshing.next(true);
						return httpClient.post(refreshUrl,{},{withCredentials:true,context})
						.pipe(
							tap(()=>{
								isRefreshing.next(false);
							}),
							switchMap(()=>httpClient.request(request.clone({context}))),
							catchError(()=>{
								isRefreshing.next(false);
								return of(response);
							})
						);
					}else{
						return isRefreshing.asObservable()
						.pipe(
							filter((value)=>!value),
							take(1),
							switchMap(()=>httpClient.request(request.clone({context})))
						);
					}
				}else return of(response);
			}else return of(response);
		}),
		catchError((error,caught)=>{
			if(error instanceof HttpErrorResponse){
				if(error.status===401){
					if(!isRefreshing.getValue()){
						isRefreshing.next(true);
						return concat(
							httpClient.post<HttpEvent<unknown>>(refreshUrl,{},{withCredentials:true,context}),
							caught
						)
						.pipe(
							tap(()=>{
								isRefreshing.next(false);
							}),
							catchError((error)=>{
								isRefreshing.next(false);
								return sendError(error as InterceptorError);
							})
						);
					}else{
						return isRefreshing.asObservable()
						.pipe(
							filter((value)=>!value),
							take(1),
							switchMap(()=>caught)
						);
					}
				}else return sendError(error as InterceptorError);
			}else return sendError(error as InterceptorError);
		})
	);
};
